import { useEffect, useState, useMemo } from 'react';
import { useAppSelector, useUserSelector } from 'js/store/hooks';
import { prop } from 'ramda';
import { useGameConfig } from 'js/components/common/Game';
import { useGameDetailsMetaTags } from './useGameDetailsMetaTags';

type UseFetchGameDetailsProps = {
    code: string;
}

const createMetaTags = ({ metaDescription, metaTitle, metaLogoUrl, metaGameUrl }: Record<string, string>) => [
    ...metaDescription ? [{ name: 'description', content: metaDescription }] : [{ name: 'description', content: '' }],
    ...metaTitle ? [{ name: 'title', content: metaTitle }] : [],
    ...metaTitle ? [{ property: 'og:title', content: `${metaTitle} | Swiss Casinos` }] : [],
    ...metaDescription ? [{ property: 'og:description', content: metaDescription }] : [],
    ...metaLogoUrl ? [{ property: 'og:image', content: metaLogoUrl }] : [],
    ...metaGameUrl ? [{ property: 'og:url', content: metaGameUrl }] : []
];

const initialGameData = {
    gameName: '',
    gameDescription: '',
    metaTitle: '',
    metaDescription: ''
};

export const useFetchGameDetails = ({ code }: UseFetchGameDetailsProps) => {
    const userLang = useUserSelector(prop('lang'));
    const metaGameUrl = window.location.href;
    const [gameData, setGameData] = useState(initialGameData);
    const dynamicTags = useAppSelector(prop('dynamicTags'));

    const { longImg }: Record<string, string> = useGameConfig(code);
    const metaLogoUrl = `${window.location.origin}${longImg}`;

    const metaTags = useMemo(() => createMetaTags({ ...gameData, metaLogoUrl, metaGameUrl }),
     [gameData, metaGameUrl, metaLogoUrl]);

     useGameDetailsMetaTags(metaTags);

    useEffect(() => {
        const fetchGameDescription = async () => {
            try {
                const lang = userLang?.slice(0, 2);
                const gameEndpoint = dynamicTags.DTAG_GAME_DESCRIPTION_URL;

                const myHeaders = new Headers();
                myHeaders.append('x-auth-token', 'sk-proj-tR-1ebddfa4X');
                myHeaders.append('Content-Type', 'application/json');

                const raw = JSON.stringify({ gamecode: code, language: lang });

                const requestOptions = {
                    method: 'POST',
                    headers: myHeaders,
                    body: raw,
                    redirect: 'follow' as RequestRedirect
                };

                if (!gameEndpoint) {
                    setGameData(initialGameData);
                    console.warn('Endpoint game description is not found');

                    return;
                }

                const response = await fetch(gameEndpoint, requestOptions);

                const text = await response.text();
                const regExp = new RegExp(/[{":,]+/g);

                if (regExp.test(text)) {
                    const parsedText = JSON.parse(text);
                    setGameData({
                        gameName: parsedText.name,
                        gameDescription: parsedText.game_description,
                        metaDescription: parsedText.meta_description,
                        metaTitle: parsedText.meta_title
                    });
                } else {
                    setGameData(initialGameData);
                    console.warn(text, ' game code: ', code);
                }

            } catch (error) {
                console.error('error', error);
            }
        };

        fetchGameDescription();

        return () => {
            setGameData(initialGameData);
        };
    }, [code, userLang, dynamicTags]);

    return gameData;
};
